import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { formatDateShort } from '@/lib/date';
import { getStatusMeta, lessonStatusMap } from '@/lib/statusMap';
import StatusBadge from '@/components/base/StatusBadge';

interface GroupRow {
  id: string;
  name: string;
  level: string | null;
  day: string | null;
  day_note: string | null;
  time: string | null;
  whatsapp_link: string | null;
  color: string | null;
  active: boolean;
}

interface ProfileRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
}

interface StudentRow {
  id: string;
  group_id: string | null;
  level: string | null;
  parent_name: string | null;
  parent_phone: string | null;
}

interface LessonRow {
  id: string;
  title: string;
  date: string | null;
  start_time: string | null;
  status: string;
  subject: string | null;
}

interface SimpleItem {
  id: string;
  title: string;
  date?: string | null;
}

interface GrupDetayModalProps {
  group: GroupRow | null;
  onClose: () => void;
}

type Tab = 'ogrenciler' | 'dersler' | 'kayitlar' | 'dokumanlar' | 'odevler' | 'duyurular';

const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: 'ogrenciler', label: 'Öğrenciler', icon: 'ri-user-line' },
  { key: 'dersler', label: 'Dersler', icon: 'ri-book-open-line' },
  { key: 'kayitlar', label: 'Ders Kayıtları', icon: 'ri-vidicon-line' },
  { key: 'dokumanlar', label: 'Dokümanlar', icon: 'ri-file-text-line' },
  { key: 'odevler', label: 'Ödevler', icon: 'ri-task-line' },
  { key: 'duyurular', label: 'Duyurular', icon: 'ri-megaphone-line' },
];

export default function GrupDetayModal({ group, onClose }: GrupDetayModalProps) {
  const [tab, setTab] = useState<Tab>('ogrenciler');
  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [lessons, setLessons] = useState<LessonRow[]>([]);
  const [records, setRecords] = useState<SimpleItem[]>([]);
  const [docs, setDocs] = useState<SimpleItem[]>([]);
  const [assignments, setAssignments] = useState<SimpleItem[]>([]);
  const [announcements, setAnnouncements] = useState<SimpleItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [addStudentOpen, setAddStudentOpen] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!group) return;
    setLoading(true);
    try {
      const groupId = group.id;
      const [
        { data: profilesData },
        { data: studentsData },
        { data: lessonsData },
        { data: recordsData },
        { data: docsData },
        { data: assignmentsData },
        { data: announcementsData },
      ] = await Promise.all([
        supabase.from('zehrahoca_profiles').select('id, full_name, email, phone, active').eq('role', 'ogrenci'),
        supabase.from('zehrahoca_students').select('id, group_id, level, parent_name, parent_phone'),
        supabase.from('zehrahoca_lessons').select('id, title, date, start_time, status, subject').eq('group_id', groupId).order('date', { ascending: false }),
        supabase.from('zehrahoca_lesson_records').select('id, title, date').eq('group_id', groupId).order('date', { ascending: false }),
        supabase.from('zehrahoca_documents').select('id, title, upload_date').eq('group_id', groupId),
        supabase.from('zehrahoca_assignments').select('id, title, sent_date').eq('group_id', groupId),
        supabase.from('zehrahoca_announcements').select('id, title, date').eq('group_id', groupId),
      ]);
      setProfiles((profilesData ?? []) as ProfileRow[]);
      setStudents((studentsData ?? []) as StudentRow[]);
      setLessons((lessonsData ?? []) as LessonRow[]);
      setRecords(((recordsData ?? []) as SimpleItem[]).map((r) => ({ ...r, date: r.date })));
      setDocs(((docsData ?? []) as SimpleItem[]).map((d) => ({ ...d, date: (d as unknown as { upload_date: string }).upload_date })));
      setAssignments(((assignmentsData ?? []) as SimpleItem[]).map((a) => ({ ...a, date: (a as unknown as { sent_date: string }).sent_date })));
      setAnnouncements((announcementsData ?? []) as SimpleItem[]);
    } catch {
      // hata durumunda boş bırak
    } finally {
      setLoading(false);
    }
  }, [group]);

  useEffect(() => {
    if (group) {
      setTab('ogrenciler');
      fetchAll();
    }
  }, [group, fetchAll]);

  const profileMap = useMemo(() => {
    const m: Record<string, ProfileRow> = {};
    profiles.forEach((p) => { m[p.id] = p; });
    return m;
  }, [profiles]);

  const groupStudents = useMemo(
    () => students.filter((s) => s.group_id === group?.id),
    [students, group],
  );

  const availableStudents = useMemo(
    () => students.filter((s) => s.group_id !== group?.id),
    [students, group],
  );

  if (!group) return null;

  async function removeStudent(studentId: string) {
    await supabase.from('zehrahoca_students').update({ group_id: null }).eq('id', studentId);
    window.dispatchEvent(new Event('zehrahoca:groups-changed'));
    window.dispatchEvent(new Event('zehrahoca:students-changed'));
    fetchAll();
  }

  async function addStudent(studentId: string) {
    if (!studentId) return;
    setAddingStudent(true);
    try {
      await supabase.from('zehrahoca_students').update({ group_id: group.id }).eq('id', studentId);
      setAddStudentOpen(false);
      window.dispatchEvent(new Event('zehrahoca:groups-changed'));
      window.dispatchEvent(new Event('zehrahoca:students-changed'));
      fetchAll();
    } finally {
      setAddingStudent(false);
    }
  }

  function openNewLesson() {
    window.dispatchEvent(
      new CustomEvent('zehrahoca:open-lesson-modal', { detail: { groupId: group.id } }),
    );
    onClose();
  }

  const color = group.color ?? '#6366f1';

  return (
    <Modal
      open={!!group}
      onClose={onClose}
      title={group.name}
      subtitle={group.level ? `${group.level} grubu` : 'Grup detayları'}
      maxWidth="max-w-3xl"
    >
      <div className="space-y-5">
        {/* Üst bilgi kartı */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg bg-background-50 p-3">
            <p className="text-xs text-foreground-500">Seviye</p>
            <p className="text-sm font-semibold text-foreground-900">{group.level ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-background-50 p-3">
            <p className="text-xs text-foreground-500">Ders Günü</p>
            <p className="text-sm font-semibold text-foreground-900">{group.day ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-background-50 p-3">
            <p className="text-xs text-foreground-500">Ders Saati</p>
            <p className="text-sm font-semibold text-foreground-900">{group.time ?? '—'}</p>
          </div>
          <div className="rounded-lg bg-background-50 p-3">
            <p className="text-xs text-foreground-500">Öğrenci</p>
            <p className="text-sm font-semibold text-foreground-900">{groupStudents.length} kişi</p>
          </div>
        </div>

        {(group.day_note || group.whatsapp_link) && (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            {group.day_note && (
              <p className="flex items-center gap-2 text-xs text-foreground-600">
                <i className="ri-information-line text-foreground-400" />
                {group.day_note}
              </p>
            )}
            {group.whatsapp_link && (
              <a
                href={group.whatsapp_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-600 cursor-pointer"
              >
                <i className="ri-whatsapp-line" /> WhatsApp Grubu
              </a>
            )}
          </div>
        )}

        {/* Sekmeler */}
        <div className="flex flex-wrap gap-1 rounded-full bg-background-100 p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition cursor-pointer ${
                tab === t.key ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
              }`}
            >
              <i className={t.icon} />
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-500" />
          </div>
        ) : (
          <>
            {tab === 'ogrenciler' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground-800">Bu gruba bağlı öğrenciler</p>
                  <button
                    type="button"
                    onClick={() => setAddStudentOpen(true)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
                  >
                    <i className="ri-user-add-line" /> Öğrenci Ekle
                  </button>
                </div>
                {groupStudents.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-background-300 p-8 text-center">
                    <i className="ri-user-search-line text-2xl text-foreground-300" />
                    <p className="mt-2 text-xs text-foreground-500">Bu grupta henüz öğrenci yok.</p>
                  </div>
                ) : (
                  <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                    {groupStudents.map((s) => {
                      const profile = profileMap[s.id];
                      const fullName = profile?.full_name ?? 'Bilinmeyen Öğrenci';
                      const initial = (fullName || '?').trim().charAt(0).toUpperCase();
                      return (
                        <div key={s.id} className="flex items-center justify-between rounded-lg border border-background-200 p-3">
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 font-semibold text-primary-700">
                              {initial}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground-950">{fullName}</p>
                              <p className="text-xs text-foreground-500">
                                {profile?.phone ?? '—'} · {profile?.email ?? '—'}
                              </p>
                            </div>
                            {profile && (
                              <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${profile.active ? 'bg-emerald-50 text-emerald-600' : 'bg-background-100 text-foreground-500'}`}>
                                {profile.active ? 'Aktif' : 'Pasif'}
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeStudent(s.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500 transition hover:bg-red-100 cursor-pointer"
                            title="Gruptan Çıkar"
                          >
                            <i className="ri-user-unfollow-line text-base" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {tab === 'dersler' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground-800">Bu gruba ait dersler</p>
                  <button
                    type="button"
                    onClick={openNewLesson}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-primary-600 cursor-pointer"
                  >
                    <i className="ri-add-line" /> Yeni Ders Ekle
                  </button>
                </div>
                {lessons.length === 0 ? (
                  <div className="rounded-lg border border-dashed border-background-300 p-8 text-center">
                    <i className="ri-book-open-line text-2xl text-foreground-300" />
                    <p className="mt-2 text-xs text-foreground-500">Bu gruba henüz ders eklenmemiş.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {lessons.map((l) => (
                      <div key={l.id} className="flex items-center justify-between rounded-lg border border-background-200 p-3">
                        <div className="flex items-center gap-3">
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
                          <div>
                            <p className="text-sm font-semibold text-foreground-950">{l.title}</p>
                            {l.subject && <p className="text-xs text-foreground-500">{l.subject}</p>}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-foreground-500">
                            {l.date ? formatDateShort(l.date) : '—'}
                            {l.start_time ? ` · ${l.start_time}` : ''}
                          </span>
                          <StatusBadge meta={getStatusMeta(l.status, lessonStatusMap)} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(tab === 'kayitlar' || tab === 'dokumanlar' || tab === 'odevler' || tab === 'duyurular') && (
              <div className="space-y-2">
                {(() => {
                  const list =
                    tab === 'kayitlar' ? records :
                    tab === 'dokumanlar' ? docs :
                    tab === 'odevler' ? assignments : announcements;
                  if (list.length === 0) {
                    return (
                      <div className="rounded-lg border border-dashed border-background-300 p-8 text-center">
                        <p className="text-xs text-foreground-500">Bu grupta henüz içerik yok.</p>
                      </div>
                    );
                  }
                  return list.map((item) => (
                    <div key={item.id} className="flex items-center justify-between rounded-lg border border-background-200 p-3">
                      <p className="text-sm font-medium text-foreground-900">{item.title}</p>
                      {item.date && <span className="text-xs text-foreground-500">{formatDateShort(item.date)}</span>}
                    </div>
                  ));
                })()}
              </div>
            )}
          </>
        )}
      </div>

      {/* Öğrenci Ekle alt modalı */}
      <Modal
        open={addStudentOpen}
        onClose={() => setAddStudentOpen(false)}
        title="Öğrenci Ekle"
        subtitle={`${group.name} grubuna mevcut bir öğrenciyi bağlayın.`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            addStudent((fd.get('student') as string || '').trim());
          }}
          className="space-y-5"
        >
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Öğrenci Seç</label>
            <select
              name="student"
              defaultValue=""
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="" disabled>
                {availableStudents.length === 0 ? 'Bağlanabilecek öğrenci yok' : 'Öğrenci seçin'}
              </option>
              {availableStudents.map((s) => {
                const profile = profileMap[s.id];
                return (
                  <option key={s.id} value={s.id}>
                    {profile?.full_name ?? 'Bilinmeyen Öğrenci'}
                  </option>
                );
              })}
            </select>
            {availableStudents.length === 0 && (
              <p className="mt-2 text-xs text-foreground-500">
                Tüm öğrenciler zaten bu grupta. Önce Öğrenciler bölümünden yeni öğrenci ekleyin.
              </p>
            )}
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
            <button
              type="button"
              onClick={() => setAddStudentOpen(false)}
              className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={addingStudent || availableStudents.length === 0}
              className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
            >
              {addingStudent ? 'Ekleniyor...' : 'Gruba Ekle'}
            </button>
          </div>
        </form>
      </Modal>
    </Modal>
  );
}